<script setup>
import { computed, onMounted, ref } from "vue"

import Lottie from "@/components/widgets/lottie.vue"
import animationData from "@/components/widgets/msoeawqm.json"
import * as persianTools from "@persian-tools/persian-tools"
import PersianDate from "persian-date"

import store from "../../../state/store"
import useTransactions from "../../../use/useTransactions"
import useDebouncedFunction from "../../../use/useDebouncedFunction"

const transactions = computed(() => store.state.transactions)

const { transactionStatus, search, perPage, getTransactions } =
  useTransactions()
const { handleInput } = useDebouncedFunction(getTransactions)

const formattedPrices = computed(() => {
  return transactions.value.data.map((transaction) =>
    new Intl.NumberFormat("fa-IR").format(transaction.paid)
  )
})

onMounted(() => {
  getTransactions()
})

const statusLabels = {
  STATUS_FAILED: "تراکنش ناموفق",
  STATUS_PENDING: "در حال انجام",
  STATUS_SUCCESS: "تراکنش موفق",
}

const transactionTypeLabels = {
  1: "شارژ کیف پول",
  2: "پرداخت سفارش",
}

const toPersianDateTime = (date) => {
  const [datePart, timePart] = date.split(" ")
  const [year, month, day] = datePart.split("-")
  const [hour, minute, second] = timePart.split(":")
  const gregorianDate = new Date(year, month - 1, day, hour, minute, second)
  const persianDate = new PersianDate(gregorianDate)
  return persianDate.format("YYYY/MM/DD HH:mm:ss")
}

function getForPage(ev, link) {
  ev.preventDefault()
  if (!link.url || link.active) {
    return
  }
  getTransactions(link.url)
}

const defaultOptions = ref({
  animationData: animationData,
})
</script>

<template>
  <b-card-body class="border-bottom-dashed border-bottom">
    <b-row class="g-3">
      <b-col xl="7">
        <div class="search-box">
          <input
            v-model="search"
            @input="handleInput"
            type="text"
            class="form-control search"
            placeholder="جستجوی شماره تراکنش ..."
          />
          <i class="ri-search-line search-icon"></i>
        </div>
      </b-col>

      <b-col xl="5">
        <b-row class="g-3">
          <b-col sm="6">
            <select
              @change="getTransactions(null)"
              v-model="transactionStatus"
              class="form-select mb-3"
              aria-label="Default select example"
            >
              <option value="" selected>انتخاب وضعیت تراکنش</option>
              <option
                v-for="(label, value) in statusLabels"
                :key="value"
                :value="value"
              >
                {{ label }}
              </option>
            </select>
          </b-col>

          <b-col sm="6">
            <select
              @change="getTransactions(null)"
              v-model="perPage"
              class="form-select mb-3"
              aria-label="Default select example"
            >
              <option value="" selected>نمایش تعداد ردیف</option>
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="30">30</option>
              <option value="50">50</option>
            </select>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </b-card-body>
  <b-card-body>
    <div>
      <div class="table-responsive table-card mb-1">
        <table class="table align-middle" id="transactionTable">
          <thead class="table-light text-muted">
            <tr>
              <th class="sort" data-sort="paymentId">شماره تراکنش</th>
              <th class="sort" data-sort="user">نام مشتری</th>
              <th class="sort" data-sort="paid">مبلغ تراکنش</th>
              <th class="sort" data-sort="type">نوع تراکنش</th>
              <th class="sort" data-sort="trackingCode">شماره سفارش</th>
              <th class="sort" data-sort="status">وضعیت</th>
              <th class="sort" data-sort="dateTime">زمان تراکنش</th>
            </tr>
          </thead>
          <tbody
            class="list"
            v-if="transactions.loading || !transactions.data.length"
          >
            <tr>
              <td colspan="8">
                <div class="text-center">
                  <b-spinner
                    v-if="transactions.loading"
                    variant="dark"
                    style="width: 3rem; height: 3rem"
                  ></b-spinner>
                  <div v-else>
                    <lottie
                      class="avatar-xl"
                      colors="primary:#121331,secondary:#08a88a"
                      :options="defaultOptions"
                      :height="75"
                      :width="75"
                    />
                    <h5 class="mt-2">هیچ تراکنشی وجود ندارد!</h5>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>

          <tbody class="list" v-else>
            <tr v-for="(transaction, index) of transactions.data" :key="index">
              <td class="paymentId">
                {{ transaction.paymentId }}
              </td>
              <td class="user">{{ transaction.user }}</td>
              <td class="paid">{{ formattedPrices[index] }}</td>
              <td class="type">
                {{ transactionTypeLabels[transaction.type] }}
              </td>
              <td class="trackingCode">
                <router-link
                  v-if="transaction.order_id"
                  :to="{
                    name: 'orders.details',
                    params: { id: transaction.order_id },
                  }"
                  class="link-primary"
                >
                  {{
                    transaction.trackingCode
                      ? persianTools.digitsEnToFa(transaction.trackingCode)
                      : ""
                  }}
                </router-link>
              </td>
              <td class="status">
                <span
                  class="badge text-uppercase"
                  :class="{
                    'badge-soft-info': transaction.status == 'STATUS_PENDING',
                    'badge-soft-success':
                      transaction.status == 'STATUS_SUCCESS',
                    'badge-soft-danger': transaction.status == 'STATUS_FAILED',
                  }"
                  >{{ statusLabels[transaction.status] }}</span
                >
              </td>
              <td class="dateTime">
                {{ toPersianDateTime(transaction.dateTime) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        class="d-flex justify-content-end"
        v-if="transactions.total > transactions.limit"
      >
        <div class="pagination-wrap hstack gap-2" style="display: flex">
          <ul class="pagination listjs-pagination mb-0">
            <li
              v-for="(link, i) of transactions.links"
              :key="i"
              class="page-item"
              :class="{
                disabled: !link.url,
                active: link.active || !link.url,
              }"
            >
              <span
                class="page-link"
                v-if="link.active"
                v-html="link.label"
              ></span>

              <a
                v-else
                href="#"
                @click="getForPage($event, link)"
                class="page-link"
                v-html="link.label"
              >
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </b-card-body>
</template>
